import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { BehaviorSubject, fromEvent, merge, Subscription, timer } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AutoLogoutService {
    private inactivityTimer: Subscription | null = null;
    private lockScreenTimer: Subscription | null = null;
    private userActivitySubscription: Subscription | null = null;
    private inactivityTimeout = 600000; // 10 minutes in milliseconds
    private lockScreenTimeout = 300000; // 5 minutes in milliseconds
    private countdownSubject = new BehaviorSubject<number>(0); // Countdown observable
    private lastRoute: string = '/'; // Default to root if undefined
    constructor(private router: Router,
        private ngZone: NgZone,
        private _authService: AuthService) {

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                if (this.router.url !== '/confirmation-required') {
                    this.lastRoute = event.urlAfterRedirects;
                }
            });
    }

    // Access countdown as an observable
    getCountdown() {
        return this.countdownSubject.asObservable();
    }

    startMonitoring() {
        this.resetTimer();
        this.monitorUserActivity();
    }

    private monitorUserActivity() {
        const userActivityEvents = ['mousemove', 'keydown', 'click', 'touchstart'];
        this.userActivitySubscription = merge(
            ...userActivityEvents.map(event => fromEvent(document, event))
        )
            .pipe(throttleTime(1000)) // Throttle to avoid excessive resets
            .subscribe(() => {
                this.resetTimer();
            });
    }

    private resetTimer() {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }

        if (this.router.url === '/confirmation-required') {
            // Prevent unnecessary reset if already on lock screen
            return;
        }

        this.inactivityTimer = timer(this.inactivityTimeout).subscribe(() => {
            this.navigateToLockScreen();
        });
    }

    private navigateToLockScreen() {
        this.router.navigate(['/confirmation-required']);
        this.startLockScreenTimer();
    }

    private startLockScreenTimer() {
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }

        // Initialize countdown value
        let countdown = this.lockScreenTimeout / 1000;

        // Emit countdown every second
        this.lockScreenTimer = timer(0, 1000).subscribe(() => {
            if (countdown <= 0) {
                this.lockScreenTimer?.unsubscribe();
                this.logoutUser();
            } else {
                this.countdownSubject.next(--countdown);
            }
        });
    }

    public continueSession() {
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }
        this.resetTimer();
    }

    private logoutUser() {
        this._authService.signOut().subscribe();
        this.router.navigate(['/sign-in']); // Navigate to login on logout
    }


    stopMonitoring() {
        if (this.inactivityTimer) {
            this.inactivityTimer.unsubscribe();
        }
        if (this.userActivitySubscription) {
            this.userActivitySubscription.unsubscribe();
        }
        if (this.lockScreenTimer) {
            this.lockScreenTimer.unsubscribe();
        }
    }

    public getLastRoute(): string {
        return this.lastRoute;
    }
}
